import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import './i18n'; // Import i18n configuration first
import { logger, configureLogger, LogLevel } from './shared/utils/logger';
import { initTracing } from './telemetry/tracing';

// Configure logger based on environment
const isDevelopment = import.meta.env.MODE === 'development';
const isProduction = import.meta.env.MODE === 'production';

// Initialize distributed tracing
initTracing();

configureLogger({
  level: isDevelopment ? LogLevel.DEBUG : LogLevel.INFO,
  environment: import.meta.env.MODE as 'development' | 'staging' | 'production',
  enableConsole: true,
  enableRemoteLogging: isProduction,
  remoteEndpoint: import.meta.env.VITE_LOGGING_ENDPOINT || undefined,
  redactSensitiveData: true,
  includeStackTrace: true,
  release: import.meta.env.VITE_APP_VERSION || 'dev',
});

// Global error handler for unhandled errors
window.addEventListener('error', (event) => {
  logger.error('Unhandled error', event.error, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    message: event.message,
  });
});

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled promise rejection', event.reason, {
    promise: String(event.promise),
  });
});

// Log app startup
logger.info('Application starting', {
  mode: import.meta.env.MODE,
  version: import.meta.env.VITE_APP_VERSION,
});

// Loading fallback for Suspense (used while i18n resources load)
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-surface-light dark:bg-surface-dark">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600" />
  </div>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Suspense fallback={<LoadingFallback />}>
      <App />
    </Suspense>
  </React.StrictMode>
);
